import type React from 'react';
import { useCallback } from 'react';

import classNames from 'classnames';
import type { TreeNode } from 'tree-model';

import type { IconName } from '@iconik-io/uad';

import type { TreeReducerModel } from '../../ducks/tree/types';
import Button from '../../ui/button/button';
import IconButton from '../../ui/icon_button/icon_button';
import LoadingBar from '../../ui/loading_bar/loading_bar';
import OpenContextMenuButton from '../../ui/table/open_context_menu_button/open_context_menu_button';
import { isCollectionMappedToStorage } from '../../utils/collections';
import { UUID_ZERO } from '../../utils/tree_handlers';
import ArrowOpener from '../arrow_opener/arrow_opener';
import DebugDump from '../debug_dump/debug_dump';
import { shouldLoadMore } from './should_load_more';
import { TreeBreadcrumbs } from './tree_breadcrumbs/tree_breadcrumbs';

import styles from './tree_row.module.css';

type TreeRowProps = {
  node: TreeNode<TreeReducerModel>;
  isParent?: (node: TreeNode<TreeReducerModel>) => boolean;
  onLoadMore: (
    node: TreeNode<TreeReducerModel>,
    event: React.MouseEvent,
  ) => void;
  onExpand: (node: TreeNode<TreeReducerModel>) => void;
  onClick?: (node: TreeNode<TreeReducerModel>) => void;
  titleRenderer?: (node: TreeNode<TreeReducerModel>) => string;
  withMenu?: boolean;
  dropTarget?: string;
  selectedIds?: string[];
  isDisabled?: boolean;
  isSearchResults?: boolean;
  shouldHaveBreadcrumbs?: boolean;
};

export function TreeRow({
  node,
  isParent,
  onLoadMore,
  onExpand,
  onClick,
  titleRenderer,
  withMenu = true,
  selectedIds,
  dropTarget,
  isDisabled = false,
  isSearchResults = false,
  shouldHaveBreadcrumbs = false,
}: TreeRowProps) {
  const { id, title, expanded, loading } = node.model;

  const path = node.getPath();
  const parentNode = path[path.length - 2];
  const isSelected = Boolean(selectedIds?.includes(id));
  const isElementParent = Boolean(isParent?.(node));
  const loadMore = shouldLoadMore(node);
  const rowLoader =
    loading && !node.children.length ? (
      <LoadingBar className={styles.top_loader} />
    ) : undefined;
  const bottomLoader =
    parentNode &&
    parentNode.model.loading &&
    parentNode.children.length &&
    loadMore ? (
      <LoadingBar className={styles.bottom_loader} />
    ) : undefined;

  let icon: IconName = isCollectionMappedToStorage(node.model)
    ? 'isg'
    : 'collection';

  const separators = [];
  let current = node;
  let parents = false;
  let itemTitle = title;
  const isRoot = node.model.id === UUID_ZERO;
  if (isRoot && isSearchResults) {
    icon = 'text_list_bullet';
    itemTitle = '';
  }

  while (current.parent) {
    const parentLength = current.parent.children.length;
    separators.unshift(
      <span
        key={current.model.id}
        className={classNames(styles.separator, {
          [styles.separator_last]:
            !parents && parentLength - 1 === current.getIndex(),
          [styles.separator_empty]:
            parents && parentLength - 1 === current.getIndex(),
        })}
      />,
    );
    parents = true;
    current = current.parent;
  }
  const handleExpandClick = useCallback(
    (event: React.MouseEvent<HTMLElement | SVGSVGElement>) => {
      event.stopPropagation();
      event.preventDefault();
      if (!loading) {
        onExpand(node);
      }
    },
    [loading, node, onExpand],
  );
  const handleEnsureExpandClick = () =>
    !loading && !expanded ? onExpand(node) : undefined;

  const rowTitle = titleRenderer ? titleRenderer(node) : itemTitle;
  const rowPaddingLeft = 20 * (path.length - 1);
  const { ancestor_collections: ancestorCollectionIds } = node.model;

  return (
    <div
      data-id={id}
      draggable="true"
      role="treeitem"
      aria-selected={isSelected}
      aria-expanded={expanded}
      aria-label={title}
      className={classNames(styles.item_tree, {
        [styles.item_selected]: isSelected,
        [styles.row_top_border]: isSearchResults && shouldHaveBreadcrumbs,
      })}
      onClick={handleEnsureExpandClick}
      tabIndex={0}
    >
      {isSearchResults && isRoot ? (
        <span className={styles.search_results_label}>Search results</span>
      ) : (
        <div
          className={classNames(styles.item_tree_drag, {
            [styles.item_tree_drop]: dropTarget === id,
          })}
        >
          <div className={styles.drop_target}>
            <div
              style={{
                paddingLeft: rowPaddingLeft,
              }}
              className={classNames(styles.item_wrapper, {
                [styles.parent]: isElementParent,
              })}
            >
              {!isSearchResults ? (
                <div className={styles.separators}>{separators}</div>
              ) : null}

              <ArrowOpener
                className={styles.arrow_opener}
                onClick={handleExpandClick}
                open={expanded}
              />
              <DebugDump label="Item" data={node.model} />
              <IconButton
                iconName={icon}
                onClick={handleExpandClick}
                className={classNames(styles.item_icon, {
                  [styles.disabled]: isDisabled,
                })}
              />
              <span
                className={classNames(styles.item_title, {
                  [styles.disabled]: isDisabled,
                })}
                onClick={() => onClick?.(node)}
              >
                {rowTitle}
              </span>
            </div>
          </div>

          {id !== '0' && id !== UUID_ZERO && withMenu ? (
            <OpenContextMenuButton />
          ) : undefined}
        </div>
      )}
      {(isSearchResults || !loading) &&
      shouldHaveBreadcrumbs &&
      ancestorCollectionIds ? (
        <TreeBreadcrumbs ancestorCollectionIds={ancestorCollectionIds} />
      ) : null}
      {loadMore && parentNode && parentNode.model.id !== '0' ? (
        <Button
          noBorder
          size="s"
          className={styles.load_more}
          disabled={Boolean(bottomLoader)}
          onClick={(event) => {
            event.stopPropagation();
            onLoadMore(parentNode, event);
          }}
        >
          See more
        </Button>
      ) : undefined}
      {rowLoader}
      {bottomLoader}
    </div>
  );
}
